import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { useSaveStaffSettings, useStaffSettings } from '@/queries/settings'
import type { StaffAdmin, StaffSettings } from '@/queries/settings'

// Below this many admins the whole list fits on screen, so a filter box is just noise.
const FILTER_THRESHOLD = 8

// Mirrors the API's check, so a typo is caught before Save.
const SLACK_USER_ID = /^[UW][A-Z0-9]{8,11}$/

function matches(admin: StaffAdmin, filter: string) {
  const q = filter.trim().toLowerCase()
  if (!q) return true
  return [admin.name, admin.email].some(v => v?.toLowerCase().includes(q))
}

function SupportTeamForm({ initial, admins }: { initial: StaffSettings; admins: StaffAdmin[] }) {
  const [selected, setSelected] = useState(() => new Set(initial.staffUserIds))
  const [slackIds, setSlackIds] = useState<Record<string, string>>(() => ({ ...initial.slackUserIds }))
  const [filter, setFilter] = useState('')
  const save = useSaveStaffSettings()

  const members = admins.filter(a => selected.has(a._id))
  const invalidSlackId = members.some(a => slackIds[a._id]?.trim() && !SLACK_USER_ID.test(slackIds[a._id].trim()))

  function toggle(id: string, checked: boolean) {
    setSelected(prev => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  async function handleSave() {
    try {
      // Keep the list's order, and drop ids of anyone who is no longer an admin so the save can't 400 on them.
      const slackUserIds = Object.fromEntries(members.map(a => [a._id, slackIds[a._id]?.trim() ?? '']).filter(([, id]) => id))
      await save.mutateAsync({ staffUserIds: members.map(a => a._id), slackUserIds })
      // The API drops IDs of deselected members, so reselecting one mustn't bring an old ID back.
      setSlackIds(slackUserIds)
      toast.success('Support team saved')
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not save the support team'))
    }
  }

  const visible = admins.filter(a => matches(a, filter))
  const selectedCount = members.length

  return (
    <div className="space-y-4">
      {admins.length > FILTER_THRESHOLD && (
        <Input
          pill
          aria-label="Filter admins"
          placeholder="Filter by name or email"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="max-w-xs"
        />
      )}

      <p className="text-sm text-muted-foreground">
        {selectedCount === 0
          ? 'No one is selected, so every Etainabl admin is on the support team.'
          : `${selectedCount} of ${admins.length} admins selected`}
      </p>

      <ul className="max-h-96 divide-y divide-border overflow-y-auto rounded-md border">
        {visible.map(admin => (
          <li key={admin._id} className="hover:bg-muted">
            <label className="flex cursor-pointer items-center gap-3 px-3 py-2">
              <input
                type="checkbox"
                checked={selected.has(admin._id)}
                onChange={e => toggle(admin._id, e.target.checked)}
                aria-label={admin.name ?? admin.email ?? admin._id}
                className="h-4 w-4"
              />
              <span className="min-w-0">
                <span className="block truncate text-sm text-foreground">{admin.name ?? admin.email ?? admin._id}</span>
                {admin.email && <span className="block truncate text-xs text-muted-foreground">{admin.email}</span>}
              </span>
            </label>
            {selected.has(admin._id) && (
              <div className="px-3 pb-2 pl-10">
                <Input
                  aria-label={`Slack member ID for ${admin.name ?? admin.email ?? admin._id}`}
                  placeholder="Slack member ID (optional)"
                  value={slackIds[admin._id] ?? ''}
                  onChange={e => setSlackIds(prev => ({ ...prev, [admin._id]: e.target.value }))}
                  aria-invalid={Boolean(slackIds[admin._id]?.trim()) && !SLACK_USER_ID.test(slackIds[admin._id].trim())}
                  className="h-8 max-w-xs text-sm"
                />
                <p className="mt-1 text-xs text-muted-foreground">In Slack: profile → ⋮ → Copy member ID</p>
              </div>
            )}
          </li>
        ))}
        {visible.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">No admins match that filter.</li>}
      </ul>

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={save.isPending || invalidSlackId}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {invalidSlackId && <span role="alert" className="text-xs text-destructive">A Slack member ID looks like U01ABCDE2FG.</span>}
        {initial.updatedAt && <span className="text-xs text-muted-foreground">Last changed {new Date(initial.updatedAt).toLocaleString()}</span>}
      </div>
    </div>
  )
}

export function SupportTeamView() {
  const { data, isLoading, error } = useStaffSettings()

  return (
    <div className="container mx-auto max-w-4xl px-6 pb-6">
      <Card>
        <CardHeader>
          <CardTitle>Support team</CardTitle>
          <p className="text-sm text-muted-foreground">
            People who receive new-ticket and assignment emails and can be assigned tickets. Leave empty to include every Etainabl admin.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {error && <p className="text-sm text-destructive">{extractErrorMessage(error, 'Could not load the support team')}</p>}
          {data && <SupportTeamForm initial={data.settings} admins={data.admins} />}
        </CardContent>
      </Card>
    </div>
  )
}
